// Stored IDScan scans on the customer details page (ENG-2625). The API returns each
// identification's scans already ordered front before back, then by attempt, with one primary.

const SIDE_LABELS = { FRONT: "Front", BACK: "Back" };

export function sideLabel(scan) {
  return SIDE_LABELS[scan.side] || scan.side;
}

/** "Front, attempt 3 - Refer" */
export function attemptCaption(scan) {
  return `${sideLabel(scan)}, attempt ${scan.attempt} - ${scan.result}`;
}

/** "Front 3 · Refer" */
export function attemptChipLabel(scan) {
  return `${sideLabel(scan)} ${scan.attempt} · ${scan.result}`;
}

export function isPassedResult(result) {
  return ["pass", "passed"].includes(
    String(result || "")
      .trim()
      .toLowerCase(),
  );
}

export function hasScans(record) {
  return Boolean(record && record.scans && record.scans.length > 0);
}

/** The scan to show first: the one the API marked primary. */
export function primaryScan(scans) {
  const list = scans || [];
  return list.find((scan) => scan.primary) || list[0] || null;
}

/** One attempt when it is selected, otherwise every attempt. */
export function selectedScans(scans, selectedImageId) {
  const list = scans || [];
  const chosen = list.filter((scan) => scan.idscanImageId === selectedImageId);
  return chosen.length > 0 ? chosen : list;
}

/** Each scan's document image, then its face portrait, captioned. Links that could not be signed are left out. */
export function scanImages(scans) {
  return (scans || []).flatMap((scan) => [
    ...(scan.documentImageUrl
      ? [
          {
            url: scan.documentImageUrl,
            caption: attemptCaption(scan),
            kind: "document",
          },
        ]
      : []),
    ...(scan.facePortraitUrl
      ? [
          {
            url: scan.facePortraitUrl,
            caption: `Face portrait - ${attemptCaption(scan)}`,
            kind: "portrait",
          },
        ]
      : []),
  ]);
}

/** A journey's scans across the customer's records, once each: a journey held by several records repeats them. */
export function journeyScans(records, journeyId) {
  const seen = new Set();
  const scans = [];
  for (const record of records || []) {
    for (const scan of record.scans || []) {
      if (scan.journeyId === journeyId && !seen.has(scan.idscanImageId)) {
        seen.add(scan.idscanImageId);
        scans.push(scan);
      }
    }
  }
  return scans;
}

/**
 * The verification logs as one entry per journey: IDScan sends several messages per journey. The
 * logs come newest first, so each journey takes its latest date and its latest non-blank result.
 * A log without a journey stays on its own.
 */
export function journeyResults(logs) {
  const journeys = [];
  const byJourney = new Map();
  for (const log of logs || []) {
    let journey = log.journeyId && byJourney.get(log.journeyId);
    if (!journey) {
      journey = {
        journeyId: log.journeyId || null,
        verificationDate: log.verificationDate,
        scanStatus: "",
        logs: [],
      };
      journeys.push(journey);
      if (log.journeyId) {
        byJourney.set(log.journeyId, journey);
      }
    }
    if (!journey.scanStatus && log.scanStatus) {
      journey.scanStatus = log.scanStatus;
    }
    journey.logs.push(log);
  }
  return journeys;
}

/**
 * The journey the IDScan section opens on: the active record's, when it has scans, otherwise the
 * newest verification whose journey has scans. Null when no journey has scans.
 */
export function defaultJourneyId(
  records,
  activeIdentificationId,
  verifications,
) {
  const active = (records || []).find(
    (record) =>
      record.identificationId === activeIdentificationId && hasScans(record),
  );
  if (active) {
    return primaryScan(active.scans).journeyId;
  }
  const newest = (verifications || []).find(
    (verification) =>
      verification.journeyId &&
      journeyScans(records, verification.journeyId).length > 0,
  );
  return newest ? newest.journeyId : null;
}
